import type React from "react";
import { useTranslation } from "react-i18next";

import type { BoardHandler, Handler } from "../../types/handler";
import { handlerInitials, handlerName } from "../../types/handler";

/**
 * ── The handler bubble ───────────────────────────────────────────────────────
 *
 * A grey circle with two initials, standing for the dispatcher dealing with a
 * transport. It appears on four screens, on an order row, a container row, a
 * planning card and a driver's message, and it is the same twenty-four pixels
 * everywhere so that "whose is this?" is answered in one glance and one place.
 *
 * ── Why it is not coloured ───────────────────────────────────────────────────
 *
 * Because colour is already spoken for, twice. Every client has a partner
 * colour that stripes its rows and edges its planning cards, and every status
 * has a tone — green returned, amber at risk, red late. A third colour system
 * on the same row would not read as a third dimension; it would read as noise,
 * and it would be competing for attention with the two that tell a dispatcher
 * whether anything is *wrong*. Whose job it is never belongs in that contest.
 *
 * So the bubble carries weight through shape and position instead, and the one
 * distinction it does draw in colour is the one that matters most often: your
 * own bubble gets a ring, so "mine" is findable in a dense table without
 * reading a single pair of letters.
 *
 * ── Initials collide, and that is survivable ─────────────────────────────────
 *
 * Twelve dispatchers will contain two MKs sooner or later. The tooltip is the
 * resolution, as it already is for the `createdBy` column this borrows its
 * abbreviation from. A third letter was considered and left alone: it would
 * make most bubbles inconsistent with each other to disambiguate a pair that
 * the hover already disambiguates.
 */

export type HandlerBubbleSize = "sm" | "md";

interface HandlerBubbleProps {
  /** The handler, in either of the two shapes the API sends. */
  handler?: Handler | BoardHandler | null;
  /** The signed-in user's id — what earns the ring. */
  meId?: string | undefined;
  size?: HandlerBubbleSize;
  /**
   * Make the empty state read as an invitation rather than as a gap.
   *
   * A dashed outline and a `+`, for the surfaces where one press claims the
   * record. Off elsewhere, where unassigned is simply a fact being reported and
   * a call to action would be a button that does nothing.
   */
  claimable?: boolean;
  /** Rendered inside a button by the caller — drop this one's own title. */
  silent?: boolean;
  className?: string;
}

const SIZES: Record<HandlerBubbleSize, string> = {
  sm: "h-5 w-5 text-[8.5px]",
  md: "h-6 w-6 text-[9.5px]",
};

export const HandlerBubble: React.FC<HandlerBubbleProps> = ({
  handler,
  meId,
  size = "md",
  claimable = false,
  silent = false,
  className = "",
}) => {
  const { t } = useTranslation("containerOrders");

  const id = handler
    ? "_id" in handler
      ? handler._id
      : handler.id
    : null;

  const initials = handlerInitials(handler);
  const name = handlerName(handler);

  if (!id) {
    return (
      <span
        aria-hidden
        title={silent ? undefined : t("handler.unassigned")}
        className={`inline-flex shrink-0 items-center justify-center rounded-full ${
          SIZES[size]
        } ${
          claimable
            ? "border-[1.5px] border-dashed border-gray-300 bg-white font-semibold text-gray-400"
            : "border border-dashed border-gray-200 bg-white text-gray-300"
        } ${className}`}
      >
        {claimable ? "+" : "–"}
      </span>
    );
  }

  /**
   * A handler whose name did not come with the record.
   *
   * The planning board's mutation endpoints answer with a card built inside a
   * transaction and do not pay for a name lookup, so a freshly dragged card can
   * arrive with an id and nothing else. Callers resolve it against the roster
   * they already hold; this is the fallback for the frame before that lands —
   * a filled bubble with a dot, rather than an empty one, because the container
   * *does* have a handler and saying otherwise would be the wrong fact.
   */
  const unresolved = !initials;

  return (
    <span
      title={silent ? undefined : name || t("handler.unknown")}
      className={`inline-flex shrink-0 items-center justify-center rounded-full bg-[#E9E5D9] font-extrabold tracking-[0.02em] text-[#3A3933] ${
        SIZES[size]
      } ${
        // Your own work, findable without reading the letters.
        meId && id === meId ? "ring-2 ring-primary ring-offset-1" : ""
      } ${className}`}
    >
      {unresolved ? "·" : initials}
    </span>
  );
};

export default HandlerBubble;
